/**
 * RecordVOPaymentModal Component
 * Modal for recording a payment against a variation order
 */

import React, { useState, useRef } from 'react';
import { variationOrderAPI, projectFilesAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-toastify';

const RecordVOPaymentModal = ({ isOpen, onClose, onSuccess, vo, projectId }) => {
  const [formData, setFormData] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    referenceNumber: '',
    notes: ''
  });
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const voAmount = parseFloat(vo.amount) || 0;
  const actualPaid = parseFloat(vo.actualPaidAmount) || 0;
  const remainingAmount = Math.max(0, voAmount - actualPaid);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      if (!isValidType) {
        toast.error(`${file.name}: Only images and PDFs are allowed`);
      }
      if (!isValidSize) {
        toast.error(`${file.name}: File size must be under 10MB`);
      }
      return isValidType && isValidSize;
    });
    setReceiptFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  // Remove file
  const removeFile = (index) => {
    setReceiptFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (!formData.paymentDate) {
      toast.error('Please select a payment date');
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);

    try {
      // Upload receipt files first
      const uploadedFiles = [];
      if (receiptFiles.length > 0) {
        for (let i = 0; i < receiptFiles.length; i++) {
          const file = receiptFiles[i];
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          formDataUpload.append('projectId', projectId);
          formDataUpload.append('fileType', 'payment_receipt');

          const uploadRes = await projectFilesAPI.upload(formDataUpload);
          if (uploadRes.data.data) {
            uploadedFiles.push({
              fileId: uploadRes.data.data.id,
              fileName: uploadRes.data.data.originalName,
              filePath: uploadRes.data.data.filePath
            });
          }
          setUploadProgress(Math.round(((i + 1) / receiptFiles.length) * 50));
        }
      }

      // Record the payment
      await variationOrderAPI.recordPayment(projectId, vo.id, {
        amount: parseFloat(formData.amount),
        paymentDate: formData.paymentDate,
        paymentMethod: formData.paymentMethod || null,
        referenceNumber: formData.referenceNumber || null,
        notes: formData.notes || null,
        receiptFiles: uploadedFiles
      });

      setUploadProgress(100);
      toast.success('Payment recorded successfully');
      onSuccess();
    } catch (err) {
      console.error('Error recording payment:', err);
      toast.error(err.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Record VO Payment
            </h3>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{vo.voNumber}</p>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
            {/* VO Info */}
            <div className="bg-purple-50 rounded-lg p-3 mb-4 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">VO Amount:</span>
                <span className="font-medium">{formatCurrency(voAmount)}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Already Paid:</span>
                <span className="font-medium text-green-600">{formatCurrency(actualPaid)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-purple-200 mt-2">
                <span className="text-gray-700 font-medium">Remaining:</span>
                <span className="font-semibold text-purple-600">{formatCurrency(remainingAmount)}</span>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Amount (RM) *
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  disabled={submitting}
                  min="0"
                  step="0.01"
                  placeholder={remainingAmount > 0 ? remainingAmount.toFixed(2) : '0.00'}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                  autoFocus
                />
                {remainingAmount > 0 && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, amount: remainingAmount.toFixed(2) })}
                    className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium min-h-[44px] px-2 -ml-2 touch-manipulation"
                  >
                    Fill remaining ({formatCurrency(remainingAmount)})
                  </button>
                )}
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date *
                </label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                  disabled={submitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  disabled={submitting}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                >
                  <option value="">Select method</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="online_payment">Online Payment</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Reference Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Number
                </label>
                <input
                  type="text"
                  value={formData.referenceNumber}
                  onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                  disabled={submitting}
                  placeholder="Transaction reference"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base min-h-[48px] touch-manipulation"
                />
              </div>

              {/* Receipt Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Receipt
                </label>
                <div
                  onClick={() => !submitting && fileInputRef.current?.click()}
                  className={`border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 active:bg-purple-100 transition-colors min-h-[80px] touch-manipulation ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600">Tap to upload receipt</p>
                  <p className="text-xs text-gray-400 mt-1">Images or PDF, max 10MB each</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* Selected files list */}
                {receiptFiles.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {receiptFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                        <div className="flex items-center gap-2 min-w-0">
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          disabled={submitting}
                          className="text-red-500 hover:text-red-700 disabled:opacity-50 ml-2 p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  disabled={submitting}
                  rows={2}
                  placeholder="Additional notes"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 text-base touch-manipulation"
                />
              </div>
            </div>

            {/* Upload Progress */}
            {submitting && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Uploading...</span>
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 active:scale-[0.98] min-h-[48px] touch-manipulation transition-all disabled:opacity-50"
            >
              {submitting ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordVOPaymentModal;
